const budget_status_class_map = {
  'Within budget': 'bg-success-subtle text-success-emphasis',
  Watch: 'bg-warning-subtle text-warning-emphasis',
  'Over budget': 'bg-danger-subtle text-danger-emphasis'
};

const override_status_class_map = {
  Approved: 'bg-success-subtle text-success-emphasis',
  'Under review': 'bg-warning-subtle text-warning-emphasis',
  Rejected: 'bg-danger-subtle text-danger-emphasis'
};

const irregular_status_class_map = {
  Open: 'bg-danger-subtle text-danger-emphasis',
  'Under review': 'bg-warning-subtle text-warning-emphasis',
  Closed: 'bg-success-subtle text-success-emphasis'
};

const irregular_type_style_map = {
  Irregular: { icon: 'bi-exclamation-triangle', icon_style: 'background:#fff2f2;color:#ef4444;border:1px solid #f9d0d0;' },
  'Fruitless & wasteful': { icon: 'bi-arrow-repeat', icon_style: 'background:#fff5e8;color:#c4580b;border:1px solid #f2dac1;' }
};

function normalized_form_text(value, fallback_value) {
  const normalized_value = (value || '').trim() || fallback_value;
  return normalized_value;
}

export const create_invoice_form = () => ({
  supplier: '',
  amount: '',
  purchase_order: '',
  budget_line: '',
  note: ''
});

export const create_budget_form = () => ({
  line: '',
  budget: '',
  committed: '',
  status: 'Within budget'
});

export const create_override_form = () => ({
  invoice: '',
  reason: '',
  authorised_by: '',
  date: '',
  status: 'Approved'
});

export const create_irregular_form = () => ({
  title: '',
  type: 'Irregular',
  invoice: '',
  amount: '',
  detail: '',
  status: 'Open'
});

export const amount_to_number = (amount) => Number(String(amount || '').replace(/[^0-9.]/g, '')) || 0;

export const amount_to_currency = (amount) => {
  const numeric = amount_to_number(amount);
  if (!numeric) return 'R 0';
  return `R ${numeric.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
};

export const invoice_to_key = (invoice) => String(invoice || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');

function has_budget_value(budget) {
  const has_budget = budget > 0;
  return has_budget;
}

function is_over_budget_amount(budget, committed) {
  const is_over_budget = committed > budget && has_budget_value(budget);
  return is_over_budget;
}

function trimmed_form_amount(value) {
  const amount_value = String(value || '').trim();
  return amount_value;
}

function calculate_utilisation(budget, committed) {
  const has_budget = has_budget_value(budget);
  const percentage = has_budget ? Math.round((committed / budget) * 100) : 0;
  const utilisation = has_budget ? Math.min(100, percentage) : 0;
  const committed_class = is_over_budget_amount(budget, committed) ? 'text-danger-emphasis' : '';

  const result = {
    utilisation,
    utilisation_label: `${percentage}%`,
    committed_class,
  };

  return result;
}

function budget_row_identity(form) {
  const budget_raw = trimmed_form_amount(form.budget);
  const committed_raw = trimmed_form_amount(form.committed);
  const identity = {
    line: (form.line || '').trim() || 'New budget line',
    budget: amount_to_currency(form.budget),
    budget_raw,
    committed: amount_to_currency(form.committed),
    committed_raw
  };
  return identity;
}

function build_status(status_value) {
  const status = {
    label: status_value,
    class:
      Reflect.get(budget_status_class_map, status_value) ??
      budget_status_class_map['Within budget'],
  };
  return status;
}

function build_override_status(status_value) {
  const override_status = {
    label: status_value,
    class:
      Reflect.get(override_status_class_map, status_value) ??
      override_status_class_map.Approved,
  };
  return override_status;
}

function build_irregular_badge_class(status_value) {
  const irregular_badge_class =
    Reflect.get(irregular_status_class_map, status_value) ??
    irregular_status_class_map.Open;
  return irregular_badge_class;
}

function resolve_irregular_type_style(type_value) {
  const style = Reflect.get(irregular_type_style_map, type_value) ??
    irregular_type_style_map.Irregular;
  return style;
}

function build_irregular_meta(type_value, invoice, detail) {
  const irregular_meta = `${type_value} - ${invoice} - ${detail}`;
  return irregular_meta;
}

function normalize_override_form(form) {
  const invoice = normalized_form_text(form.invoice, 'INV-0000');
  const reason = normalized_form_text(form.reason, 'Manual override logged');
  const authorised_by = normalized_form_text(form.authorised_by, 'Finance owner');
  const date = normalized_form_text(form.date, 'TBC');
  const normalized_override_form = {
    invoice,
    reason,
    authorised_by,
    date
  };
  return normalized_override_form;
}

function normalize_irregular_form(form) {
  const type_value = form.type || 'Irregular';
  const status_value = form.status || 'Open';
  const title = normalized_form_text(form.title, 'New expenditure flag');
  const invoice = normalized_form_text(form.invoice, 'INV-0000');
  const detail = normalized_form_text(form.detail, 'Details pending review.');
  const amount = amount_to_currency(form.amount);
  const amount_raw = String(form.amount || '').trim();
  const normalized_irregular_form = {
    type_value,
    status_value,
    title,
    invoice,
    detail,
    amount,
    amount_raw
  };
  return normalized_irregular_form;
}

export const build_budget_row = (form, existing_row = {}) => {
  const budget_value = amount_to_number(form.budget);
  const committed_value = amount_to_number(form.committed);
  const status_value = form.status || 'Within budget';
  const identity = budget_row_identity(form);

  const {
    utilisation,
    utilisation_label,
    committed_class,
  } = calculate_utilisation(budget_value, committed_value);

  const updated_row = {
    ...existing_row,
    ...identity,
    utilisation,
    utilisation_label,
    committed_class,
    status: build_status(status_value),
    status_value,
    highlighted: true,
  };

  return updated_row;
};

export function build_override_row(form, existing_row = {}) {
  const status_value = form.status || 'Approved';
  const override_status = build_override_status(status_value);
  const normalized_form = normalize_override_form(form);
  const override_row = {
    ...existing_row,
    invoice: normalized_form.invoice,
    reason: normalized_form.reason,
    authorised_by: normalized_form.authorised_by,
    date: normalized_form.date,
    status: override_status,
    status_value,
    highlighted: true
  };

  return override_row;
}

export function build_irregular_row(form, existing_row = {}) {
  const normalized_form = normalize_irregular_form(form);
  const style = resolve_irregular_type_style(normalized_form.type_value);
  const irregular_badge_class = build_irregular_badge_class(normalized_form.status_value);
  const irregular_meta = build_irregular_meta(normalized_form.type_value, normalized_form.invoice, normalized_form.detail);
  const irregular_row = {
    ...existing_row,
    title: normalized_form.title,
    type: normalized_form.type_value,
    invoice: normalized_form.invoice,
    detail: normalized_form.detail,
    meta: irregular_meta,
    badge: normalized_form.status_value,
    badge_class: irregular_badge_class,
    status_value: normalized_form.status_value,
    amount: normalized_form.amount,
    amount_raw: normalized_form.amount_raw,
    icon: style.icon,
    icon_style: style.icon_style,
    highlighted: true
  };

  return irregular_row;
}
